/** The head loads gtag once; advanced consent mode controls analytics cookies. */
export type SiteEventName = 'page_view' | 'signup_click' | 'login_click' | 'contact_click' | 'calculator_used' | 'calculator_download';
export interface SiteEvent { name: SiteEventName; page: string; placement?: 'header' | 'footer' | 'content' }
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    'ga-disable-G-8YKVJHB4JC': boolean;
  }
}
const TAG_ID = 'G-8YKVJHB4JC';
const STORAGE_KEY = 'yummo:analytics-consent:v1';
const CHOICE_LIFETIME = 180 * 24 * 60 * 60 * 1000;
const productionHost = import.meta.env.PROD && ['yummo.com.br', 'www.yummo.com.br'].includes(location.hostname);
type Choice = { analytics: boolean; expires: number };
let choice: Choice | null = null;
let currentPage = '/';
let pageViewSent = false;
let installed = false;
let expiryTimer: ReturnType<typeof setTimeout> | undefined;

function readChoice(): Choice | null {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return stored && typeof stored.analytics === 'boolean' && Number.isFinite(stored.expires) && stored.expires > Date.now() ? stored : null;
  } catch { return null; }
}
function mayMeasure() {
  return productionHost;
}
function safeReferrer() {
  try { return document.referrer ? new URL(document.referrer).origin + '/' : ''; } catch { return ''; }
}
function pageParameters() {
  return { page_location: `https://yummo.com.br${currentPage}`, page_path: currentPage, page_title: document.title, page_referrer: safeReferrer() };
}
function removeAnalyticsCookies() {
  const domains = ['', location.hostname, '.yummo.com.br', 'yummo.com.br'];
  for (const part of document.cookie.split(';')) {
    const name = part.trim().split('=')[0];
    if (name !== '_ga' && name !== '_ga_8YKVJHB4JC') continue;
    for (const domain of domains) document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}${domain ? `; Domain=${domain}` : ''}`;
  }
}
function denyAnalyticsStorage() {
  window['ga-disable-G-8YKVJHB4JC'] = !productionHost;
  window.gtag('consent', 'update', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
  removeAnalyticsCookies();
}
function allowAnalyticsStorage() {
  if (!productionHost) return;
  window['ga-disable-G-8YKVJHB4JC'] = false;
  window.gtag('consent', 'update', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
}
export function track(name: SiteEventName, placement?: SiteEvent['placement']) {
  const detail: SiteEvent = { name, page: currentPage, ...(placement ? { placement } : {}) };
  window.dispatchEvent(new CustomEvent<SiteEvent>('yummo:analytics', { detail }));
  // A click is not a completed registration. Never send form inputs or money values.
  if (!mayMeasure()) return;
  if (name === 'page_view') {
    if (pageViewSent) return;
    pageViewSent = true;
  }
  window.gtag('event', name, { ...pageParameters(), ...(placement ? { placement } : {}), send_to: TAG_ID, transport_type: 'beacon' });
}
export function installAnalytics(page: string) {
  if (installed) return;
  installed = true;
  currentPage = page;
  choice = readChoice();
  window.gtag('set', pageParameters());
  const banner = document.createElement('section');
  banner.className = 'consent-banner';
  banner.setAttribute('aria-labelledby', 'consent-title');
  banner.innerHTML = `<div class="consent-copy"><span class="consent-eyebrow">VOCÊ ESCOLHE.</span><h2 id="consent-title">Uma visita. Uma escolha sua.</h2><p>O Google Analytics recebe sinais sem cookies para medir visitas. Você também permite cookies de analytics? Recusar mantém a medição sem cookies. <a href="/privacy#cookies-e-preferencias">Saiba como funciona</a>.</p><p class="consent-current" aria-live="polite"></p></div><div class="consent-actions"><button type="button" data-consent="reject">Recusar cookies</button><button type="button" data-consent="accept">Permitir cookies</button></div><button type="button" class="consent-close" data-consent-close aria-label="Fechar preferências de cookies" hidden>×</button>`;
  banner.hidden = choice !== null;
  document.body.append(banner);
  let returnFocus: HTMLElement | null = null;
  function showPreferences(focus = false) {
    banner.hidden = false;
    banner.querySelector<HTMLButtonElement>('[data-consent-close]')!.hidden = choice === null;
    banner.querySelector<HTMLElement>('.consent-current')!.textContent = choice ? `Sua escolha atual: ${choice.analytics ? 'cookies de analytics permitidos' : 'cookies de analytics recusados'}. Você pode mudar quando quiser.` : '';
    if (focus) banner.querySelector<HTMLButtonElement>('[data-consent="reject"]')!.focus({ preventScroll: true });
  }
  function closePreferences() {
    banner.hidden = true;
    returnFocus?.focus({ preventScroll: true });
    returnFocus = null;
  }
  function applyChoice() {
    clearTimeout(expiryTimer);
    if (choice?.analytics) allowAnalyticsStorage();
    else denyAnalyticsStorage();
    if (choice) {
      // Timers are capped by browsers; re-schedule until the stored choice expires.
      expiryTimer = setTimeout(() => {
        if (choice && choice.expires <= Date.now()) {
          choice = null;
          denyAnalyticsStorage();
          showPreferences();
        } else applyChoice();
      }, Math.min(Math.max(choice.expires - Date.now(), 1), 2_147_483_647));
    }
  }
  banner.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-consent-close]')) { closePreferences(); return; }
    const button = event.target.closest<HTMLButtonElement>('[data-consent]');
    if (!button) return;
    choice = { analytics: button.dataset.consent === 'accept', expires: Date.now() + CHOICE_LIFETIME };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(choice)); } catch { /* Choice still applies to this document. */ }
    applyChoice();
    closePreferences();
  });
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const preferences = event.target.closest<HTMLElement>('[data-cookie-preferences]');
    if (preferences) {
      returnFocus = preferences;
      showPreferences(true);
      return;
    }
    const link = event.target.closest<HTMLAnchorElement>('a[href]');
    if (!link) return;
    const name = link.hasAttribute('data-signup') ? 'signup_click' : link.hasAttribute('data-login') ? 'login_click' : link.hasAttribute('data-contact') ? 'contact_click' : null;
    if (name) track(name, link.closest('header') ? 'header' : link.closest('footer') ? 'footer' : 'content');
  });
  banner.addEventListener('keydown', event => {
    if (event.key === 'Escape' && choice) closePreferences();
  });
  window.addEventListener('storage', event => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    choice = readChoice();
    applyChoice();
    if (!choice) showPreferences();
    else banner.hidden = true;
  });
  applyChoice();
  track('page_view');
}
